/**
 * محرك السياسات المحلي.
 *
 * هذا الملف يقابل سياسات RLS في PostgreSQL بالمعنى والسلوك. وجوده ضروري
 * لأن الوضع المحلي لا يمرّ على قاعدة البيانات. القواعد هنا ليست "واجهة تجميلية":
 * كل قراءة/كتابة تمرّ عبر `assertCan*`، والاختبارات تتحقق من حالات المنع.
 *
 * عند تغيير سياسة في migration، يجب تحديث الملف المقابل هنا في نفس التغيير.
 * (انظر docs/SECURITY.md → مصفوفة التطابق بين الملفين.)
 */

import { AppError } from '@/domain/errors';
import type { PermissionKey } from '@/domain/permissions';
import { canViewTask, type AccessContext, type ResolvedScope } from '@/domain/access';
import type { Task } from '@/domain/types';
import type { TableName, TablePolicy, WriteRule } from './types';

export interface PolicyContext {
  userId: string | null;
  isPlatformOwner: boolean;
  /** الشركة النشطة في الجلسة، أو null */
  companyId: string | null;
  membershipId: string | null;
  permissions: ReadonlySet<PermissionKey>;
  scope: ResolvedScope | null;
  access: AccessContext;
}

export const TABLE_POLICIES: Record<TableName, TablePolicy> = {
  profiles: {
    tenantField: null,
    readPermission: null,
    readRule: 'selfRow',
    writeRule: { kind: 'self' },
    softDelete: false,
    requiresTenant: false,
  },
  membership_profiles: {
    tenantField: 'companyId',
    readPermission: null,
    readRule: 'tenantMember',
    writeRule: { kind: 'self' },
    softDelete: false,
    requiresTenant: true,
  },
  platform_owners: {
    tenantField: null,
    readPermission: 'platform.owner.manage',
    readRule: 'platformOwner',
    writeRule: { kind: 'platformOwner' },
    softDelete: false,
    requiresTenant: false,
  },
  platform_members: {
    tenantField: null,
    readPermission: null,
    readRule: 'selfRow',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: false,
  },
  platform_member_permissions: {
    tenantField: null,
    readPermission: null,
    readRule: 'selfRow',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: false,
  },
  platform_settings: {
    tenantField: null,
    readPermission: null,
    readRule: 'platformOwner',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: false,
  },
  subscription_plans: {
    tenantField: null,
    readPermission: null,
    readRule: 'platformOwner',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: false,
  },
  company_subscriptions: {
    tenantField: 'companyId',
    readPermission: null,
    readRule: 'tenantMember',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: true,
  },
  subscription_events: {
    tenantField: 'companyId',
    readPermission: null,
    readRule: 'tenantMember',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: true,
  },
  subscription_payments: {
    tenantField: 'companyId',
    readPermission: null,
    readRule: 'tenantMember',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: true,
  },
  company_invites: {
    tenantField: 'companyId',
    readPermission: null,
    readRule: 'platformOwner',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: true,
  },
  registration_requests: {
    tenantField: null,
    readPermission: null,
    readRule: 'platformOwner',
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: false,
  },
  companies: {
    tenantField: 'id',
    readPermission: null,
    readRule: 'tenantMember',
    writeRule: { kind: 'permissions', permissions: ['company.manage', 'platform.company.manage'] },
    softDelete: true,
    requiresTenant: false,
  },
  brands: {
    tenantField: 'companyId',
    readPermission: 'company.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'brand.manage' },
    softDelete: true,
    requiresTenant: true,
  },
  branches: {
    tenantField: 'companyId',
    readPermission: 'company.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'branch.manage' },
    softDelete: true,
    requiresTenant: true,
  },
  brand_branch_links: {
    tenantField: 'companyId',
    readPermission: 'company.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'branch.manage' },
    softDelete: false,
    requiresTenant: true,
  },
  task_categories: {
    tenantField: 'companyId',
    readPermission: 'task.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'task.category.manage' },
    softDelete: false,
    requiresTenant: true,
  },
  memberships: {
    tenantField: 'companyId',
    readPermission: 'user.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'membership.manage' },
    softDelete: true,
    requiresTenant: true,
  },
  membership_placements: {
    tenantField: 'companyId',
    readPermission: 'user.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'membership.manage' },
    softDelete: true,
    requiresTenant: true,
  },
  membership_roles: {
    tenantField: 'companyId',
    readPermission: 'user.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'membership.manage' },
    softDelete: false,
    requiresTenant: false,
  },
  membership_access_grants: {
    tenantField: 'companyId',
    readPermission: 'supervision.manage',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'supervision.manage' },
    softDelete: false,
    requiresTenant: true,
  },
  roles: {
    tenantField: 'companyId',
    readPermission: 'role.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'role.manage' },
    softDelete: true,
    requiresTenant: false,
  },
  role_permissions: {
    tenantField: null,
    readPermission: 'role.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'role.manage' },
    softDelete: false,
    requiresTenant: false,
  },
  role_scopes: {
    tenantField: null,
    readPermission: 'role.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'role.manage' },
    softDelete: false,
    requiresTenant: false,
  },
  supervision_relations: {
    tenantField: 'companyId',
    readPermission: 'supervision.manage',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'supervision.manage' },
    softDelete: false,
    requiresTenant: true,
  },
  task_templates: {
    tenantField: 'companyId',
    readPermission: 'task.view',
    readRule: 'taskScoped',
    writeRule: { kind: 'permission', permission: 'task.template.manage' },
    softDelete: true,
    requiresTenant: true,
  },
  task_template_assignees: {
    tenantField: null,
    readPermission: 'task.view',
    readRule: 'taskScoped',
    writeRule: { kind: 'permission', permission: 'task.template.manage' },
    softDelete: false,
    requiresTenant: false,
  },
  tasks: {
    tenantField: 'companyId',
    readPermission: 'task.view',
    readRule: 'taskScoped',
    writeRule: { kind: 'taskScoped' },
    softDelete: true,
    requiresTenant: true,
  },
  task_assignees: {
    tenantField: null,
    readPermission: 'task.view',
    readRule: 'taskScoped',
    writeRule: { kind: 'taskScoped' },
    softDelete: false,
    requiresTenant: false,
  },
  task_steps: {
    tenantField: null,
    readPermission: 'task.view',
    readRule: 'taskScoped',
    writeRule: { kind: 'taskScoped' },
    softDelete: false,
    requiresTenant: false,
  },
  task_status_history: {
    tenantField: null,
    readPermission: 'task.view',
    readRule: 'taskScoped',
    // سجل الحالات يُكتب من الأوامر الخادمية فقط، ولا يُعدَّل ولا يُحذف.
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: false,
  },
  attachments: {
    tenantField: 'companyId',
    readPermission: 'task.view',
    readRule: 'taskScoped',
    writeRule: { kind: 'permission', permission: 'attachment.upload' },
    softDelete: true,
    requiresTenant: true,
  },
  attachment_deletions: {
    tenantField: 'companyId',
    readPermission: 'attachment.retention.manage',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'attachment.retention.manage' },
    softDelete: false,
    requiresTenant: true,
  },
  notifications: {
    tenantField: 'companyId',
    readPermission: null,
    readRule: 'selfRow',
    // لا يُنشئ المستخدم إشعارات لنفسه أو لغيره عبر الواجهة؛
    // الإشعارات تُنشأ داخل الأوامر الخادمية فقط.
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: true,
  },
  settings: {
    tenantField: null,
    readPermission: null,
    readRule: 'tenantMember',
    writeRule: { kind: 'permissions', permissions: ['company.settings.manage', 'platform.settings.manage'] },
    softDelete: false,
    requiresTenant: false,
  },
  company_modules: {
    tenantField: 'companyId',
    readPermission: 'company.view',
    readRule: 'tenantMember',
    writeRule: { kind: 'permission', permission: 'platform.module.manage' },
    softDelete: false,
    requiresTenant: true,
  },
  audit_logs: {
    tenantField: 'companyId',
    readPermission: 'audit.view',
    readRule: 'tenantMember',
    // يُكتب من طبقة التدقيق الموثوقة فقط (append-only من منظور التطبيق).
    writeRule: { kind: 'deny' },
    softDelete: false,
    requiresTenant: false,
  },
};

function rowTenantId(table: TableName, row: Record<string, unknown>): string | null {
  if (table === 'companies') return (row['id'] as string | undefined) ?? null;
  const policy = TABLE_POLICIES[table];
  if (!policy.tenantField) return null;
  return (row[policy.tenantField] as string | undefined) ?? null;
}

export function isSameTenant(ctx: PolicyContext, tenantId: string | null): boolean {
  if (ctx.isPlatformOwner) return true;
  if (tenantId === null) return false;
  return ctx.companyId !== null && ctx.companyId === tenantId;
}

export function isTenantMember(ctx: PolicyContext, tenantId: string | null): boolean {
  if (tenantId === null) return ctx.isPlatformOwner;
  if (ctx.companyId === tenantId) return true;
  // عضو في أكثر من شركة: تكفي وجود العضوية في العضويات المحمّلة
  return ctx.access.memberships.some((m) => m.companyId === tenantId);
}

export function hasPermission(ctx: PolicyContext, permission: PermissionKey): boolean {
  return ctx.permissions.has(permission);
}

/* =============================================================================
 * القراءة
 * ========================================================================== */

export interface RowReadHint {
  /** المهمة الأب عند قراءة جدول مرتبط بمهمة */
  parentTask?: Task | null;
  /** شركة السجل الأب عند غياب حقل companyId في الجدول نفسه */
  parentCompanyId?: string | null;
  /** معرّفات العضويات المسندة إلى المهمة فعليًا */
  assigneeMembershipIds?: string[];
  /** العضويات التي أكملت المهمة فعليًا */
  completerMembershipIds?: string[];
  /** نطاق العضوية الهدف، لفرض عزل الفروع والعلامات عند قراءة الموظفين */
  membershipUserId?: string | null;
  membershipBranchIds?: string[];
  membershipBrandIds?: string[];
}

export function canReadRow(
  ctx: PolicyContext,
  table: TableName,
  row: Record<string, unknown>,
  hint?: RowReadHint,
): { allowed: boolean; reason: string } {
  const policy = TABLE_POLICIES[table];

  if (!ctx.userId) return { allowed: false, reason: 'لا توجد جلسة' };

  // العزل عبر الشركة الأب (جداول مرتبطة لا تحمل companyId بنفسها)
  if (hint?.parentCompanyId && !isSameTenant(ctx, hint.parentCompanyId)) {
    return { allowed: false, reason: 'السجل التابع ينتمي إلى شركة أخرى' };
  }

  // منع الوصول عبر الشركة أولًا: أهم قاعدة في النظام
  if (policy.requiresTenant) {
    const tenantId = rowTenantId(table, row);
    if (tenantId && !isSameTenant(ctx, tenantId)) {
      return { allowed: false, reason: 'السجل ينتمي إلى شركة أخرى' };
    }
  }

  switch (policy.readRule) {
    case 'public':
      return { allowed: true, reason: 'قراءة عامة' };
    case 'platformOwner':
      return ctx.isPlatformOwner
        ? { allowed: true, reason: 'مالك المنصة' }
        : { allowed: false, reason: 'يتطلب صلاحية مالك المنصة' };
    case 'selfRow': {
      if (table === 'profiles') {
        if (row['id'] === ctx.userId) return { allowed: true, reason: 'ملفي الشخصي' };
        if (ctx.isPlatformOwner && hasPermission(ctx, 'user.view')) {
          return { allowed: true, reason: 'مالك المنصة' };
        }
        if (hasPermission(ctx, 'user.view') && hint?.parentCompanyId === ctx.companyId && ctx.companyId) {
          return { allowed: true, reason: 'عرض مستخدمي الشركة' };
        }
        return { allowed: false, reason: 'ملف مستخدم آخر' };
      }
      if (table === 'notifications') {
        if (row['recipientUserId'] === ctx.userId) return { allowed: true, reason: 'إشعاراتي' };
        return { allowed: false, reason: 'إشعار لمستخدم آخر' };
      }
      if (table === 'platform_members' || table === 'platform_member_permissions') {
        if (row['userId'] === ctx.userId || ctx.isPlatformOwner) return { allowed: true, reason: 'صلاحيات المنصة الخاصة' };
        return { allowed: false, reason: 'سجل منصة لمستخدم آخر' };
      }
      return { allowed: false, reason: 'غير مسموح' };
    }
    case 'tenantMember': {
      if (table === 'companies') {
        if (ctx.isPlatformOwner) return { allowed: true, reason: 'مالك المنصة' };
        if (isTenantMember(ctx, row['id'] as string)) return { allowed: true, reason: 'عضو في الشركة' };
        return { allowed: false, reason: 'لست عضوًا في هذه الشركة' };
      }
      if (policy.readPermission && !hasPermission(ctx, policy.readPermission)) {
        // استثناء: قراءة بياناتك التنظيمية بحد أدنى
        if (!isSelfRelatedRow(ctx, table, row)) {
          return { allowed: false, reason: 'لا تملك صلاحية القراءة' };
        }
      }
      const tenantId = rowTenantId(table, row);
      if (tenantId === null) {
        // جداول بلا معرّف شركة (roles، settings، role_permissions) تُفلتر لاحقًا
        return { allowed: true, reason: 'قراءة مقيدة بالسياق' };
      }
      if (!isSameTenant(ctx, tenantId)) {
        return { allowed: false, reason: 'السجل خارج شركتك' };
      }
      if (
        (table === 'memberships' || table === 'membership_placements') &&
        !membershipRowInScope(ctx, table, row, hint)
      ) {
        return { allowed: false, reason: 'العضو أو ارتباطه خارج نطاق فرعك أو علامتك' };
      }
      return { allowed: true, reason: 'قراءة داخل الشركة' };
    }
    case 'taskScoped':
      return canReadTaskScopedRow(ctx, table, row, hint);
    default:
      return { allowed: false, reason: 'سياسة غير معروفة' };
  }
}

/**
 * يطابق app.scope_covers_membership على الخادم: العضويات تُقرأ ضمن نطاق
 * الفروع/العلامات، لا لمجرد امتلاك صلاحية user.view على المنشأة.
 */
function membershipRowInScope(
  ctx: PolicyContext,
  table: 'memberships' | 'membership_placements',
  row: Record<string, unknown>,
  hint?: RowReadHint,
): boolean {
  if (ctx.isPlatformOwner || ctx.scope?.companyWide) return true;
  const membershipId = table === 'memberships' ? String(row['id'] ?? '') : String(row['membershipId'] ?? '');
  const rowUserId =
    table === 'memberships' ? (row['userId'] as string | undefined) : hint?.membershipUserId;
  if (rowUserId && rowUserId === ctx.userId) return true;
  if (membershipId && membershipId === ctx.membershipId) return true;
  if (!ctx.scope) return false;
  if (membershipId && ctx.scope.membershipIds.has(membershipId)) return true;
  if ((hint?.membershipBranchIds ?? []).some((id) => ctx.scope?.branchIds.has(id))) return true;
  // نطاق العلامة المكتسب من فرع لا يفتح بقية الفروع؛ نطلب منحًا صريحًا للعلامة.
  const directBrandIds = new Set<string>();
  const membership = ctx.access.membership;
  if (membership) {
    for (const role of membership.roles) {
      for (const scope of role.scopes) {
        if (scope.scopeType === 'brand') directBrandIds.add(scope.targetId ?? '*');
      }
    }
    for (const grant of membership.grants) {
      if (grant.revokedAt === null && grant.scopeType === 'brand') {
        directBrandIds.add(grant.targetId ?? '*');
      }
    }
  }
  if (
    directBrandIds.has('*') ||
    (hint?.membershipBrandIds ?? []).some((id) => directBrandIds.has(id))
  ) {
    return true;
  }
  return false;
}

function isSelfRelatedRow(
  ctx: PolicyContext,
  table: TableName,
  row: Record<string, unknown>,
): boolean {
  if (table === 'memberships') return row['userId'] === ctx.userId;
  if (table === 'roles') return true; // اسم الدور ليس سريًا، لكن التفاصيل تحتاج role.view
  if (table === 'brands' || table === 'branches' || table === 'task_categories') return true;
  return false;
}

function canReadTaskScopedRow(
  ctx: PolicyContext,
  table: TableName,
  row: Record<string, unknown>,
  hint?: RowReadHint,
): { allowed: boolean; reason: string } {
  if (!ctx.membershipId) return { allowed: false, reason: 'لا توجد عضوية نشطة' };

  if (table === 'tasks') {
    const assigneeIds = hint?.assigneeMembershipIds ?? [];
    return canViewTask({
      ctx: ctx.access,
      scope: ctx.scope ?? emptyScope(),
      subject: {
        task: row as unknown as TaskViewSubject,
        assigneeMembershipIds: assigneeIds,
        completerMembershipIds: hint?.completerMembershipIds ?? [],
      },
    });
  }

  // القوالب ليست مهامًا تنفيذية: تُقرأ ضمن نطاق الفروع/العلامات المسموح بها.
  if (table === 'task_templates') {
    const tenantId = (row['companyId'] as string | undefined) ?? null;
    if (!isSameTenant(ctx, tenantId)) {
      return { allowed: false, reason: 'القالب ينتمي إلى شركة أخرى' };
    }
    const scope = ctx.scope;
    if (!scope) return { allowed: false, reason: 'لا يوجد نطاق وصول محدد' };
    if (scope.companyWide) return { allowed: true, reason: 'قالب داخل نطاق الشركة' };
    const branchId = (row['branchId'] as string | undefined) ?? null;
    const brandId = (row['brandId'] as string | undefined) ?? null;
    const createdBy = (row['createdByMembershipId'] as string | undefined) ?? null;
    if (branchId && scope.branchIds.has(branchId)) {
      return { allowed: true, reason: 'قالب داخل نطاق فرعك' };
    }
    if (brandId && scope.brandIds.has(brandId)) {
      return { allowed: true, reason: 'قالب داخل نطاق علامتك' };
    }
    if (createdBy && scope.membershipIds.has(createdBy)) {
      return { allowed: true, reason: 'قالب أنشأته' };
    }
    return { allowed: false, reason: 'القالب خارج نطاق وصولك' };
  }

  // إسنادات القوالب تُقرأ تبعًا لقالبها ضمن نفس الشركة.
  if (table === 'task_template_assignees') {
    return hint?.parentCompanyId === ctx.companyId
      ? { allowed: true, reason: 'إسناد تابع لقالب في شركتك' }
      : { allowed: false, reason: 'إسناد تابع لقالب خارج شركتك' };
  }

  // الجداول المرتبطة (الإسناد، الخطوات، السجل، المرفقات) تُقرأ
  // بصلاحية المهمة الأب نفسها. لا نستخدم تخمينًا: إن لم تُمرَّر المهمة الأب
  // فالقراءة مرفوضة، وهذا هو السلوك الآمن.
  const parent = hint?.parentTask;
  if (!parent) {
    // المرفقات المرتبطة بمهمة غير موجودة تُرفض؛ أما مرفقات السياق العام
    // (شعار الشركة مثلًا) فتُقرأ بصلاحية الشركة فقط.
    const tenantId = row['companyId'] as string | undefined;
    if (tenantId && tenantId === ctx.companyId && row['taskId'] === null) {
      return { allowed: true, reason: 'مرفق خاص بالشركة' };
    }
    return { allowed: false, reason: 'لا يمكن تحقق صلاحية الوصول إلى المهمة الأب' };
  }

  const decision = canViewTask({
    ctx: ctx.access,
    scope: ctx.scope ?? emptyScope(),
    subject: {
      task: parent as unknown as TaskViewSubject,
      assigneeMembershipIds: hint?.assigneeMembershipIds ?? [ctx.membershipId],
      completerMembershipIds: hint?.completerMembershipIds ?? [],
    },
  });
  if (!decision.allowed) return { allowed: false, reason: 'المهمة الأب خارج نطاق وصولك' };
  return { allowed: true, reason: 'تابع لمهمة مرئية' };
}

type TaskViewSubject = Parameters<typeof canViewTask>[0]['subject']['task'];

function emptyScope(): ResolvedScope {
  return {
    companyWide: false,
    branchIds: new Set(),
    brandIds: new Set(),
    membershipIds: new Set(),
    supervisionCapabilities: {},
  };
}

/* =============================================================================
 * الكتابة
 * ========================================================================== */

export function canWriteRow(
  ctx: PolicyContext,
  table: TableName,
  operation: 'insert' | 'update' | 'delete',
  row: Record<string, unknown>,
  patch?: Record<string, unknown>,
  hint?: RowReadHint,
): { allowed: boolean; reason: string } {
  const policy = TABLE_POLICIES[table];
  if (!ctx.userId) return { allowed: false, reason: 'لا توجد جلسة' };

  const effectiveRow = operation === 'update' && patch ? { ...row, ...patch } : row;

  if (
    table === 'roles' &&
    ((operation === 'insert' && effectiveRow['deletedAt'] != null) ||
      (operation === 'update' && row['deletedAt'] !== effectiveRow['deletedAt']))
  ) {
    return { allowed: false, reason: 'حذف الدور يجب أن يمر عبر تأكيد الأثر الآمن' };
  }

  if (table === 'role_permissions') {
    const touchesRoleManagement =
      row['permissionKey'] === 'role.manage' || effectiveRow['permissionKey'] === 'role.manage';
    if (touchesRoleManagement && ctx.access.membership?.membership.isCompanyOwner !== true) {
      return { allowed: false, reason: 'منح أو سحب صلاحية إدارة الأدوار محصور بمالك المنشأة' };
    }
  }

  if (policy.requiresTenant) {
    const tenantId = rowTenantId(table, effectiveRow);
    if (tenantId && !isSameTenant(ctx, tenantId)) {
      return { allowed: false, reason: 'محاولة كتابة في شركة أخرى' };
    }
    if (!tenantId && operation === 'insert') {
      return { allowed: false, reason: 'معرّف الشركة مطلوب' };
    }
  }

  return applyWriteRule(ctx, table, policy.writeRule, operation, effectiveRow, hint);
}

function applyWriteRule(
  ctx: PolicyContext,
  table: TableName,
  rule: WriteRule,
  operation: 'insert' | 'update' | 'delete',
  row: Record<string, unknown>,
  hint?: RowReadHint,
): { allowed: boolean; reason: string } {
  switch (rule.kind) {
    case 'deny':
      return { allowed: false, reason: 'الكتابة غير مسموحة على هذا الجدول' };
    case 'platformOwner':
      return ctx.isPlatformOwner
        ? { allowed: true, reason: 'مالك المنصة' }
        : { allowed: false, reason: 'يتطلب صلاحية مالك المنصة' };
    case 'appendOnly':
      if (operation !== 'insert') {
        return { allowed: false, reason: 'هذا الجدول للإضافة فقط ولا يُعدَّل أو يُحذف' };
      }
      return { allowed: true, reason: 'إضافة سجل' };
    case 'self':
      if (table === 'profiles') {
        const targetId = (row['id'] as string | undefined) ?? ctx.userId;
        if (targetId === ctx.userId) return { allowed: true, reason: 'تعديل ملفي الشخصي' };
        if (hasPermission(ctx, 'user.manage')) return { allowed: true, reason: 'إدارة المستخدمين' };
        return { allowed: false, reason: 'لا يمكنك تعديل ملف مستخدم آخر' };
      }
      if (table === 'membership_profiles') {
        return row['userId'] === ctx.userId
          ? { allowed: true, reason: 'تعديل ملفي في المنشأة' }
          : { allowed: false, reason: 'لا يمكنك تعديل ملف عضو آخر' };
      }
      return { allowed: false, reason: 'غير مسموح' };
    case 'permission':
      if (table === 'attachments') {
        const isOwnUpload = Boolean(ctx.userId && row['uploadedByUserId'] === ctx.userId);
        const canDelete = hasPermission(ctx, 'attachment.delete') || hasPermission(ctx, 'attachment.retention.manage');
        if (hasPermission(ctx, rule.permission) || isOwnUpload || (operation === 'update' && canDelete)) {
          return { allowed: true, reason: 'صلاحية عضو المنشأة على المرفق' };
        }
      }
      return hasPermission(ctx, rule.permission)
        ? { allowed: true, reason: `صلاحية ${rule.permission}` }
        : { allowed: false, reason: 'لا تملك الصلاحية المطلوبة' };
    case 'permissions': {
      if (ctx.isPlatformOwner) return { allowed: true, reason: 'مالك المنصة' };
      const found = rule.permissions.some((p) => hasPermission(ctx, p));
      return found
        ? { allowed: true, reason: 'صلاحية كافية' }
        : { allowed: false, reason: 'لا تملك الصلاحية المطلوبة' };
    }
    case 'taskScoped': {
      if (!ctx.membershipId) return { allowed: false, reason: 'لا توجد عضوية نشطة' };
      if (operation === 'delete') {
        return { allowed: false, reason: 'لا يُحذف سجل المهمة؛ استخدم الإلغاء أو تغيير الحالة' };
      }
      // المنفذ يستطيع تحديث خطوات مهمته (تأشير الإنجاز) دون صلاحية تعديل المهمة.
      const isAssignee = hint?.assigneeMembershipIds?.includes(ctx.membershipId) ?? false;
      if (table === 'task_steps' && isAssignee) {
        return { allowed: true, reason: 'تحديث خطوات مهمة مسندة إليك' };
      }
      if (!hasPermission(ctx, 'task.create') && !hasPermission(ctx, 'task.update')) {
        return { allowed: false, reason: 'لا تملك صلاحية كتابة المهام' };
      }
      // الجداول التابعة (الإسناد، الخطوات، المرفقات) لا تحمل بيانات المهمة،
      // لذا نستخدم المهمة الأب التي يحلّها المخزن بدل الحكم على صف تابع كمهمة.
      const taskLike = (hint?.parentTask ?? row) as unknown as TaskViewSubject;
      const assigneeIds =
        hint?.assigneeMembershipIds && hint.assigneeMembershipIds.length > 0
          ? hint.assigneeMembershipIds
          : [ctx.membershipId];
      const decision = canViewTask({
        ctx: ctx.access,
        scope: ctx.scope ?? emptyScope(),
        subject: {
          task: taskLike,
          assigneeMembershipIds: assigneeIds,
          completerMembershipIds: hint?.completerMembershipIds ?? [],
        },
      });
      if (!decision.allowed) return { allowed: false, reason: 'المهمة خارج نطاق وصولك' };
      return { allowed: true, reason: 'كتابة على مهمة داخل نطاقك' };
    }
    default:
      return { allowed: false, reason: 'قاعدة كتابة غير معروفة' };
  }
}

/* =============================================================================
 * مساعدات الاستخدام
 * ========================================================================== */

export function assertCanRead(
  ctx: PolicyContext,
  table: TableName,
  row: Record<string, unknown>,
  hint?: RowReadHint,
): void {
  const decision = canReadRow(ctx, table, row, hint);
  if (!decision.allowed) {
    throw AppError.forbidden(`لا يمكن قراءة هذا السجل: ${decision.reason}`, { table });
  }
}

export function assertCanWrite(
  ctx: PolicyContext,
  table: TableName,
  operation: 'insert' | 'update' | 'delete',
  row: Record<string, unknown>,
  patch?: Record<string, unknown>,
): void {
  const decision = canWriteRow(ctx, table, operation, row, patch);
  if (!decision.allowed) {
    throw AppError.forbidden(`لا يمكن تنفيذ العملية: ${decision.reason}`, { table, operation });
  }
}

/** تنقية الصفوف المسموح بقراءتها فقط. */
export function filterReadable(
  ctx: PolicyContext,
  table: TableName,
  rows: Record<string, unknown>[],
  hintFor?: (row: Record<string, unknown>) => RowReadHint | undefined,
): Record<string, unknown>[] {
  return rows.filter((row) => canReadRow(ctx, table, row, hintFor?.(row)).allowed);
}

/** سياسة RLS في قاعدة البيانات يجب أن تطابق هذه القائمة (للاستخدام في الوثائق والاختبار). */
export const POLICY_MIRROR_NOTE =
  'كل قاعدة في TABLE_POLICIES يقابلها سياسة في supabase/migrations مطبّقة على الجداول.';
